import {
  MAX_LOCATION_NAME,
  parseLocations,
  type LocationBook,
  type ShelfLocation,
} from './locations';
import { SORT_DIRECTIONS, SORT_KEYS, type SortDirection, type SortKey } from './search';
import { WISHLIST_SORT_KEYS, type WishlistSortKey } from './wishlist';

/**
 * Settings that belong to the shelf rather than to the browser.
 *
 * Theme and language stay local — they are about the device in your hand.
 * These are about the collection itself: where the discs physically are, and
 * the order you like to look at them in. So they live in Firestore, and follow
 * you to whatever you next pick up. One document, because they are read
 * together and a document read is the unit Firestore charges for.
 */

export interface CollectionSortPreference {
  key: SortKey;
  direction: SortDirection;
}

export interface WishlistSortPreference {
  key: WishlistSortKey;
  direction: SortDirection;
}

export interface AppSettings extends LocationBook {
  collectionSort: CollectionSortPreference;
  wishlistSort: WishlistSortPreference;
}

export const DEFAULT_SETTINGS: AppSettings = {
  locations: [],
  defaultLocationId: null,
  collectionSort: { key: 'title', direction: 'asc' },
  wishlistSort: { key: 'added', direction: 'desc' },
};

function oneOf<T extends string>(input: unknown, allowed: readonly T[], fallback: T): T {
  return typeof input === 'string' && (allowed as readonly string[]).includes(input)
    ? (input as T)
    : fallback;
}

/**
 * Read whatever is in the document, and never throw.
 *
 * The document can be older than the app reading it, or absent entirely on a
 * shelf that has never opened Settings, so every field falls back on its own.
 */
export function normalizeSettings(value: unknown): AppSettings {
  if (typeof value !== 'object' || value === null) return DEFAULT_SETTINGS;
  const raw = value as Record<string, unknown>;

  const locations = parseLocations(raw.locations);

  // A default pointing at a location that is gone is no default at all; the
  // first one takes over, so there is always somewhere for an unmarked copy
  // to be.
  const claimed = typeof raw.defaultLocationId === 'string' ? raw.defaultLocationId : null;
  const defaultLocationId =
    claimed !== null && locations.some((entry) => entry.id === claimed)
      ? claimed
      : (locations[0]?.id ?? null);

  const collection = (raw.collectionSort ?? {}) as Record<string, unknown>;
  const wishlist = (raw.wishlistSort ?? {}) as Record<string, unknown>;

  return {
    locations,
    defaultLocationId,
    collectionSort: {
      key: oneOf<SortKey>(collection.key, SORT_KEYS, DEFAULT_SETTINGS.collectionSort.key),
      direction: oneOf<SortDirection>(
        collection.direction,
        SORT_DIRECTIONS,
        DEFAULT_SETTINGS.collectionSort.direction,
      ),
    },
    wishlistSort: {
      key: oneOf<WishlistSortKey>(
        wishlist.key,
        WISHLIST_SORT_KEYS,
        DEFAULT_SETTINGS.wishlistSort.key,
      ),
      direction: oneOf<SortDirection>(
        wishlist.direction,
        SORT_DIRECTIONS,
        DEFAULT_SETTINGS.wishlistSort.direction,
      ),
    },
  };
}

/**
 * The first location added becomes the default, so a shelf is never in the
 * state of having locations but nowhere for an unmarked copy to be.
 */
export function withLocation(settings: AppSettings, location: ShelfLocation): AppSettings {
  return {
    ...settings,
    locations: [...settings.locations, location],
    defaultLocationId: settings.defaultLocationId ?? location.id,
  };
}

export function withoutLocation(settings: AppSettings, id: string): AppSettings {
  const locations = settings.locations.filter((entry) => entry.id !== id);
  return {
    ...settings,
    locations,
    defaultLocationId:
      settings.defaultLocationId === id ? (locations[0]?.id ?? null) : settings.defaultLocationId,
  };
}

export function withRenamedLocation(settings: AppSettings, id: string, name: string): AppSettings {
  const trimmed = name.trim().slice(0, MAX_LOCATION_NAME);
  if (trimmed === '') return settings;
  return {
    ...settings,
    locations: settings.locations.map((entry) =>
      entry.id === id ? { ...entry, name: trimmed } : entry,
    ),
  };
}

export function withDefaultLocation(settings: AppSettings, id: string): AppSettings {
  if (!settings.locations.some((entry) => entry.id === id)) return settings;
  return { ...settings, defaultLocationId: id };
}
